import { Box, Text, VStack } from '@chakra-ui/react';
import { ButtonText, Checkbox, SearchInput } from '@morressier/alembic';
import { useState } from 'react';

interface FilterSubItem {
  value: string;
  name: string;
}

interface FilterGroupItem {
  value: string;
  name: string;
  items?: FilterSubItem[];
}

interface FilterCheckboxListProps {
  name: string;
  itemLabel: string;
  items: FilterGroupItem[];
  width?: string;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  clearSelection: () => void;
  'data-testid'?: string;
}

export default function FilterCheckboxList({
  name,
  itemLabel,
  items,
  width = '100%',
  selectedValues,
  onChange,
  clearSelection,
  'data-testid': dataTestId
}: FilterCheckboxListProps) {
  const [search, setSearch] = useState('');

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleGroupToggle = (group: FilterGroupItem) => {
    const childValues = (group.items ?? []).map(c => c.value);

    const allSelected = childValues.every(v => selectedValues.includes(v));

    if (allSelected) {
      onChange(selectedValues.filter(v => !childValues.includes(v)));
    } else {
      const merged = Array.from(new Set([...selectedValues, ...childValues]));
      onChange(merged);
    }
  };

  const getGroupCheckState = (
    group: FilterGroupItem
  ): { isChecked: boolean; isIndeterminate: boolean } => {
    const childValues = (group.items ?? []).map(c => c.value);
    if (childValues.length === 0)
      return { isChecked: false, isIndeterminate: false };

    const selectedCount = childValues.filter(v =>
      selectedValues.includes(v)
    ).length;

    return {
      isChecked: selectedCount === childValues.length,
      isIndeterminate: selectedCount > 0 && selectedCount < childValues.length
    };
  };

  // Flatten every selectable leaf value (children of grouped items, otherwise
  // the item itself) so "Select All" covers the whole group regardless of search.
  const allValues = items.flatMap(group =>
    group.items && group.items.length > 0
      ? group.items.map(child => child.value)
      : [group.value]
  );

  const allSelectedCount = allValues.filter(v =>
    selectedValues.includes(v)
  ).length;
  const areAllSelected =
    allValues.length > 0 && allSelectedCount === allValues.length;
  const isSomeSelected =
    allSelectedCount > 0 && allSelectedCount < allValues.length;

  const handleSelectAllToggle = () => {
    if (areAllSelected) {
      onChange([]);
    } else {
      onChange(allValues);
    }
  };

  const searchLower = search.toLowerCase();

  const filteredGroups = items
    .map(group => {
      const hasGroupMatch = group.name.toLowerCase().includes(searchLower);

      if (group.items && group.items.length > 0) {
        const filteredChildren = hasGroupMatch
          ? group.items
          : group.items.filter(child =>
              child.name.toLowerCase().includes(searchLower)
            );

        return filteredChildren.length > 0
          ? { ...group, items: filteredChildren }
          : null;
      }

      return hasGroupMatch ? group : null;
    })
    .filter(Boolean) as FilterGroupItem[];

  const totalResults = filteredGroups.reduce(
    (acc, group) => acc + (group.items?.length ?? 1),
    0
  );

  return (
    <Box w={width} fontSize="sm" data-testid={dataTestId}>
      {/* Search */}
      <SearchInput
        placeholder={`Search ${itemLabel.toLowerCase()}`}
        value={search}
        onChange={e => setSearch(e.target.value)}
        onInputClear={() => setSearch('')}
        name={name}
      />

      {/* Clear selections */}
      <ButtonText onClick={clearSelection} variant="secondary" mb="2" mt="2">
        Clear Selections
      </ButtonText>

      {/* Grouped checkbox list */}
      <VStack align="start" spacing={2}>
        
      {/* Select / deselect all */}
      {allValues.length > 0 && (
        <Checkbox
          name="selectAll"
          id="selectAll"
          ariaLabel="Select All"
          isChecked={areAllSelected}
          isIndeterminate={isSomeSelected}
          onChange={handleSelectAllToggle}
          mb="2"
        >
          {areAllSelected ? 'Deselect All' : 'Select All'}
        </Checkbox>
      )}
        {filteredGroups.map(group => {
          const hasChildren = group.items && group.items.length > 0;

          if (hasChildren) {
            const { isChecked, isIndeterminate } = getGroupCheckState(group);

            return (
              <Box key={group.value} w="100%">
                <Checkbox
                  name={group.name}
                  id={group.value}
                  ariaLabel={group.name}
                  isChecked={isChecked}
                  isIndeterminate={isIndeterminate}
                  onChange={() => handleGroupToggle(group)}
                >
                  {group.name} (Category)
                </Checkbox>

                <VStack align="start" spacing={1} pl={6} mt={1}>
                  {group.items!.map(child => (
                    <Checkbox
                      key={child.value}
                      name={child.name}
                      id={child.value}
                      ariaLabel={child.name}
                      isChecked={selectedValues.includes(child.value)}
                      onChange={() => handleToggle(child.value)}
                    >
                      {child.name}
                    </Checkbox>
                  ))}
                </VStack>
              </Box>
            );
          }
          return (
            <Checkbox
              key={group.value}
              name={group.name}
              id={group.value}
              ariaLabel={group.name}
              isChecked={selectedValues.includes(group.value)}
              onChange={() => handleToggle(group.value)}
            >
              {group.name}
            </Checkbox>
          );
        })}

        {totalResults === 0 && (
          <Text color="gray.500" fontSize="xs">
            No results found
          </Text>
        )}
      </VStack>
    </Box>
  );
}
